/**
 * Buy Spirits Direct — Theme JS
 * Handles cart, ReCharge subscription widget, gallery, age gate
 */

'use strict';

const QTY_MAX = 20;

/* ===== Age Gate ===== */
class AgeGate {
  constructor() {
    this.cookieKey = 'bsd_age_verified';
    this.init();
  }

  init() {
    if (this.getCookie(this.cookieKey)) return;
    this.render();
  }

  render() {
    const gate = document.createElement('div');
    gate.className = 'age-gate';
    gate.id = 'age-gate';
    gate.innerHTML = `
      <div class="age-gate__card">
        <div class="age-gate__title">Welcome to Buy Spirits Direct</div>
        <p class="age-gate__body">
          Your direct source for premium craft spirits from the country's top distilleries.
          By entering, you confirm you are of legal drinking age in your country.
        </p>
        <div class="age-gate__actions">
          <button class="btn btn--primary btn--lg" id="age-gate-yes">Yes, I'm of age</button>
          <button class="btn btn--secondary btn--lg" id="age-gate-no">No, take me back</button>
        </div>
        <p class="age-gate__disclaimer">
          Must be 21+ to purchase alcohol. Please drink responsibly.<br>
          © Buy Spirits Direct
        </p>
      </div>
    `;
    document.body.appendChild(gate);

    document.getElementById('age-gate-yes').addEventListener('click', () => {
      this.setCookie(this.cookieKey, '1', 30);
      gate.style.opacity = '0';
      gate.style.transition = 'opacity 0.5s ease';
      setTimeout(() => gate.remove(), 500);
    });

    document.getElementById('age-gate-no').addEventListener('click', () => {
      window.location.href = 'https://www.responsibility.org/';
    });
  }

  setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/`;
  }

  getCookie(name) {
    const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
    return v ? v[2] : null;
  }
}


/* ===== Cart ===== */
class BSD_Cart {
  constructor() {
    this.cartCountEls = document.querySelectorAll('[data-cart-count]');
    this.notification = document.getElementById('cart-notification');
    this.init();
  }

  init() {
    this.fetchCount();
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (btn) this.addItem(btn);

      const quickAdd = e.target.closest('[data-quick-add]');
      if (quickAdd) this.addItem(quickAdd);
    });
  }

  async fetchCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.updateCount(cart.item_count);
    } catch (e) {
      console.warn('Cart fetch failed:', e);
    }
  }

  async addItem(btn) {
    const variantId = btn.dataset.variantId || btn.dataset.addToCart;
    if (!variantId) return;

    // Read quantity from adjacent input, capped at QTY_MAX
    const qtyInput = btn.closest('.product-info__add')?.querySelector('input[name="quantity"]');
    let qty = parseInt(qtyInput?.value || btn.dataset.quantity || 1);
    qty = Math.min(Math.max(qty, 1), QTY_MAX);

    // Check current cart quantity for this variant and enforce cap
    try {
      const cartRes = await fetch('/cart.js');
      const cartData = await cartRes.json();
      const existing = cartData.items.find(i => String(i.variant_id) === String(variantId));
      const currentQty = existing ? existing.quantity : 0;
      const allowed = QTY_MAX - currentQty;
      if (allowed <= 0) {
        this.showNotification(`Maximum of ${QTY_MAX} per item already in cart.`, 'error');
        return;
      }
      qty = Math.min(qty, allowed);
    } catch(e) {
      // If cart check fails, proceed normally
    }

    const subscriptionData = this.getSubscriptionData();

    btn.disabled = true;
    const originalText = btn.textContent.trim();
    btn.textContent = 'Adding…';

    const body = {
      id: variantId,
      quantity: qty,
      properties: {}
    };

    if (subscriptionData.type === 'subscribe') {
      body.properties['subscription_id'] = subscriptionData.subscriptionId;
      body.properties['shipping_interval_frequency'] = subscriptionData.frequency;
      body.properties['shipping_interval_unit_type'] = subscriptionData.unit;
      body.properties['charge_interval_frequency'] = subscriptionData.frequency;
    }

    try {
      const res = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (data.status) {
        this.showNotification(data.description || 'Could not add item.', 'error');
      } else {
        await this.fetchCount();
        this.showNotification(`${data.title} added to your bag`, 'success');
      }
    } catch (err) {
      this.showNotification('Something went wrong. Please try again.', 'error');
    } finally {
      btn.disabled = false;
      btn.textContent = btn.dataset.defaultText || originalText || 'Add to Cart';
    }
  }

  getSubscriptionData() {
    const selected = document.querySelector('.subscription-option input:checked');
    if (!selected || selected.value === 'one-time') {
      return { type: 'one-time' };
    }

    const freqSelect = document.querySelector('[data-subscription-frequency]');
    const [frequency, unit] = freqSelect ? freqSelect.value.split('|') : ['30', 'day'];

    return {
      type: 'subscribe',
      frequency,
      unit,
      subscriptionId: selected.dataset.subscriptionId || ''
    };
  }

  updateCount(count) {
    this.cartCountEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  showNotification(message, type = 'success') {
    if (!this.notification) return;
    this.notification.innerHTML = `
      <div style="display:flex;align-items:center;gap:12px;">
        <span style="font-size:1.25rem">${type === 'success' ? '✓' : '⚠'}</span>
        <span style="font-size:0.875rem;color:var(--color-text)">${message}</span>
      </div>
    `;
    this.notification.style.borderColor = type === 'success'
      ? 'var(--color-gold)' : 'rgba(220,80,80,0.4)';
    this.notification.classList.add('active');
    setTimeout(() => this.notification.classList.remove('active'), 3500);
  }
}


/* ===== Product Gallery ===== */
class ProductGallery {
  constructor(el) {
    this.el = el;
    this.main = el.querySelector('[data-gallery-main] img');
    this.thumbs = el.querySelectorAll('[data-gallery-thumb]');
    this.init();
  }

  init() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(i));
    });
  }

  setActive(index) {
    const thumb = this.thumbs[index];
    if (!thumb) return;

    this.thumbs.forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');

    const imgSrc = thumb.querySelector('img')?.src;
    if (this.main && imgSrc) {
      this.main.style.opacity = '0';
      this.main.style.transition = 'opacity 0.25s ease';
      setTimeout(() => {
        this.main.src = imgSrc;
        this.main.style.opacity = '1';
      }, 250);
    }
  }
}


/* ===== Subscription Widget ===== */
class SubscriptionWidget {
  constructor(el) {
    this.el = el;
    this.options = el.querySelectorAll('.subscription-option input');
    this.freqWrap = el.querySelector('[data-frequency-wrap]');
    this.savingsEl = el.querySelector('[data-savings-display]');
    this.priceEl = document.querySelector('[data-current-price]');
    this.basePrice = parseFloat(this.priceEl?.dataset.basePrice || 0);
    this.discountPct = parseFloat(el.dataset.discountPct || 15);
    this.init();
  }

  init() {
    this.options.forEach(opt => {
      opt.addEventListener('change', () => this.toggle(opt));
    });
    const checked = this.el.querySelector('.subscription-option input:checked');
    if (checked) this.toggle(checked);
  }

  toggle(opt) {
    const isSubscribe = opt.value === 'subscribe';

    if (this.freqWrap) this.freqWrap.style.display = isSubscribe ? 'block' : 'none';
    if (this.savingsEl) this.savingsEl.style.display = isSubscribe ? 'flex' : 'none';

    if (this.priceEl && isSubscribe) {
      const discount = this.basePrice * (this.discountPct / 100);
      const newPrice = this.basePrice - discount;
      this.priceEl.textContent = '$' + newPrice.toFixed(2);
      if (this.savingsEl) {
        this.savingsEl.querySelector('[data-savings-amount]').textContent =
          `Save $${discount.toFixed(2)} (${this.discountPct}%) with Subscribe & Save`;
      }
    } else if (this.priceEl) {
      this.priceEl.textContent = '$' + this.basePrice.toFixed(2);
    }
  }
}


/* ===== Quantity Selector ===== */
function initQuantitySelectors() {
  document.querySelectorAll('.quantity-selector').forEach(el => {
    const input = el.querySelector('input');
    const dec   = el.querySelector('[data-qty-dec]');
    const inc   = el.querySelector('[data-qty-inc]');

    // Enforce max attribute on the input
    if (input) input.max = QTY_MAX;

    if (dec) dec.addEventListener('click', () => {
      const v = parseInt(input.value) || 1;
      input.value = Math.max(1, v - 1);
    });

    if (inc) inc.addEventListener('click', () => {
      const v = parseInt(input.value) || 1;
      if (v >= QTY_MAX) {
        // Flash the input to signal the cap
        input.style.color = 'var(--color-gold)';
        setTimeout(() => input.style.color = '', 600);
        return;
      }
      input.value = v + 1;
    });

    // Also guard manual keyboard entry
    if (input) input.addEventListener('change', () => {
      let v = parseInt(input.value) || 1;
      input.value = Math.min(Math.max(v, 1), QTY_MAX);
    });
  });
}


/* ===== Mobile Nav ===== */
function initMobileNav() {
  // Handled by header section script to avoid double-binding
}


/* ===== Scroll Reveal ===== */
function initScrollReveal() {
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('[data-reveal]').forEach(el => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
    el.style.transitionDelay = el.dataset.revealDelay || '0s';
    observer.observe(el);
  });

  const style = document.createElement('style');
  style.textContent = '[data-reveal].revealed { opacity: 1 !important; transform: none !important; }';
  document.head.appendChild(style);
}


/* ===== ReCharge Widget Init ===== */
function initReCharge() {
  window.addEventListener('recharge:widget:initialized', () => {
    const rcWidget = document.querySelector('.rca-widget__container');
    if (rcWidget) rcWidget.setAttribute('data-bsd-styled', 'true');
  });

  window.addEventListener('recharge:subscription:selected', () => {
    const widget = document.querySelector('[data-subscription-widget]');
    if (widget) {
      const subsInstance = widget.__subscriptionWidget;
      if (subsInstance) subsInstance.toggle({ value: 'subscribe' });
    }
  });
}


/* ===== Init ===== */
document.addEventListener('DOMContentLoaded', () => {
  if (document.body.dataset.ageGate !== 'false') new AgeGate();
  new BSD_Cart();
  document.querySelectorAll('[data-product-gallery]').forEach(el => new ProductGallery(el));
  document.querySelectorAll('[data-subscription-widget]').forEach(el => {
    const instance = new SubscriptionWidget(el);
    el.__subscriptionWidget = instance;
  });
  initQuantitySelectors();
  initMobileNav();
  initScrollReveal();
  initReCharge();
  document.querySelector('#MainContent')?.classList.add('page-enter');
});
